import {
  CoreContainerProperties,
  CoreContainerState,
  withAuthorPanelSwitch,
  withStandardBaseCssClass
} from '@adobe/aem-core-components-react-spa'
import { ComponentMapping, Container } from '@adobe/aem-react-editable-components'
import { Constants } from '@adobe/aem-spa-page-model-manager'

import ReactMultiProductTeaser from './ReactMultiProductTeaser'
import ReactMultiPSUTeaser from './ReactMultiPSUTeaser'
import ReactMultiTeaser from './ReactMultiTeaser'
import ReactProductComparisonTeaser from './ReactProductComparisonTeaser'
import ReactStandardTeaser from './ReactStandardTeaser'
import { BackgroundVariation, ContentFragments, ReactTeaserProps, TeaserCategory, TeaserType, Variation } from './types'
import { removeEmptyCF } from '../../utils/rule-builder-util'
import { getSpacing } from '../../utils/spacing-util'
import IsInEditorText from '../shared/IsInEditorText'

interface ReactTeaserState extends CoreContainerState {
  activeIndex: number
  previousIndex: number
}

class ReactTeaser extends Container<ReactTeaserProps, ReactTeaserState> {
  interval = 0

  constructor(props: ReactTeaserProps) {
    super(props)
    this.state = {
      activeIndex: 0,
      previousIndex: 0,
      componentMapping: this.props.componentMapping || ComponentMapping
    }
  }

  componentDidUpdate(prevProps: Readonly<CoreContainerProperties>): void {
    if (
      this.props.activeIndexFromAuthorPanel !== undefined &&
      prevProps.activeIndexFromAuthorPanel != this.props.activeIndexFromAuthorPanel
    ) {
      this.setState({ activeIndex: this.props.activeIndexFromAuthorPanel })
    }
  }

  getProductTeaser = (contentFragments: ContentFragments) => {
    const {
      cardType,
      description,
      eyebrow,
      title,
      ruleExpression = '',
      contentType,
      ruleType,
      condition,
      tokenProperties,
      priceTagIcon,
      priceDropdownIcon,
      priceDropdownIconAlt,
      variation,
      backgroundVariation,
      maxSlides,
      isInEditor = false
    } = this.props

    switch (cardType) {
      case TeaserType.comparison:
        return (
          <ReactProductComparisonTeaser
            cardType={cardType}
            priceTagIcon={priceTagIcon}
            priceDropdownIcon={priceDropdownIcon}
            priceDropdownIconAlt={priceDropdownIconAlt}
            contentFragments={contentFragments}
            ruleType={ruleType}
            ruleExpression={ruleExpression}
            tokenProperties={tokenProperties}
            condition={condition}
            contentType={contentType}
            variation={variation as unknown as Variation}
            maxSlides={maxSlides}
            backgroundVariation={backgroundVariation as BackgroundVariation}
            isInEditor={isInEditor}
          />
        )
      default:
        // Returns ReactMultiProductTeaser for TeaserType.exposedoneup, TeaserType.exposedtwoup, TeaserType.exposedthreeup:
        return (
          <ReactMultiProductTeaser
            cardType={cardType as TeaserType}
            description={description}
            eyebrow={eyebrow}
            title={title}
            ruleExpression={ruleExpression}
            ruleType={ruleType}
            condition={condition}
            contentFragments={contentFragments}
            contentType={contentType}
            tokenProperties={tokenProperties}
            priceTagIcon={priceTagIcon}
            isInEditor={isInEditor}
          />
        )
    }
  }

  getPSUTeaser = (contentFragments: ContentFragments) => {
    const {
      cardType,
      description,
      eyebrow,
      title,
      ruleExpression = '',
      contentType,
      ruleType,
      condition,
      tokenProperties,
      priceTagIcon,
      isInEditor = false
    } = this.props

    return (
      <>
        <ReactMultiPSUTeaser
          cardType={cardType as TeaserType}
          description={description}
          eyebrow={eyebrow}
          title={title}
          ruleExpression={ruleExpression}
          ruleType={ruleType}
          condition={condition}
          contentFragments={contentFragments}
          contentType={contentType}
          tokenProperties={tokenProperties}
          priceTagIcon={priceTagIcon}
          isInEditor={isInEditor}
        />
      </>
    )
  }

  getTeaser = (contentFragments: ContentFragments) => {
    const {
      cardType,
      variation,
      description,
      eyebrow,
      title,
      ruleExpression = '',
      ruleType,
      condition,
      contentType,
      tokenProperties,
      priceTagIcon,
      backgroundVariation,
      imageAlignment,
      isInEditor = false,
      hasBorder = false,
      columnWidth,
      hideImageOrIcon = false
    } = this.props

    if (cardType === TeaserType.standard || cardType === TeaserType.commerce || cardType === TeaserType.featured) {
      return (
        <ReactStandardTeaser
          variation={variation as unknown as Variation}
          ruleExpression={ruleExpression}
          ruleType={ruleType}
          condition={condition}
          cardType={cardType}
          contentFragments={contentFragments}
          contentType={contentType}
          tokenProperties={tokenProperties}
          priceTagIcon={priceTagIcon}
          backgroundVariation={backgroundVariation as BackgroundVariation}
          imageAlignment={imageAlignment}
          isInEditor={isInEditor}
        />
      )
    }

    return (
      <ReactMultiTeaser
        cardType={cardType as TeaserType}
        variation={variation as unknown as Variation}
        description={description}
        eyebrow={eyebrow}
        title={title}
        ruleExpression={ruleExpression}
        ruleType={ruleType}
        condition={condition}
        contentFragments={contentFragments}
        contentType={contentType}
        tokenProperties={tokenProperties}
        isInEditor={isInEditor}
        hasBorder={hasBorder}
        columnWidth={columnWidth}
        hideImageOrIcon={hideImageOrIcon}
      />
    )
  }

  displayItem(item: JSX.Element, index: number) {
    const isActive = index === this.state.activeIndex
    //we display the item if active is true, or if we are in the author mode. we need to always display the item for the author mode to work properly.
    const display = !!(isActive || this.props.isInEditor)

    const cssClass = isActive
      ? `${this.props.baseCssClass}__item ${this.props.baseCssClass}__item--active`
      : `${this.props.baseCssClass}__item`

    return (
      <div key={'item-' + index} className={cssClass} role='tabpanel' data-cmp-hook-teaser='item'>
        {display && item}
      </div>
    )
  }

  renderTeaser() {
    const { cqItems = {}, cqItemsOrder = [], teaserType, cardType, isInEditor = false } = this.props
    const itemsOrder = (cqItemsOrder.length > 0 ? cqItemsOrder : this.props[Constants.ITEMS_ORDER_PROP]) || []
    const items: any = (Object.keys(cqItems).length > 0 ? cqItems : this.props[Constants.ITEMS_PROP]) || {}
    const contentFragments: any = removeEmptyCF(itemsOrder.map((i: string) => items[i]))

    if (teaserType === TeaserCategory.product) {
      if (cardType === TeaserType.featured) {
        return (
          <>
            {this.childComponents.map((childComponent, index) => this.displayItem(childComponent, index))}
            {this.getTeaser(contentFragments)}
          </>
        )
      } else {
        return (
          <>
            {this.childComponents.map((childComponent, index) => this.displayItem(childComponent, index))}
            {this.getProductTeaser(contentFragments)}
          </>
        )
      }
    } else if (teaserType === TeaserCategory.psu) {
      return (
        <>
          {this.childComponents.map((childComponent, index) => this.displayItem(childComponent, index))}
          {this.getPSUTeaser(contentFragments)}
        </>
      )
    } else if (teaserType === TeaserCategory.teaser) {
      return (
        <>
          {this.childComponents.map((childComponent, index) => this.displayItem(childComponent, index))}
          {this.getTeaser(contentFragments)}
        </>
      )
    }
    return (
      <>
        {IsInEditorText(isInEditor, 'React Teaser')}
        {this.childComponents.map((childComponent, index) => this.displayItem(childComponent, index))}
        {isInEditor && <p className='editor-text'>Please select teaser type</p>}
      </>
    )
  }

  render() {
    const {
      top = false,
      right = false,
      bottom = false,
      left = false,
      lob = '',
      hideMCP = false,
      isInEditor = false
    } = this.props

    // Checks if hideMCP flag is true and that display is in dispatcher, not in author:
    const mcpClass = hideMCP && isInEditor == false ? 'd-none' : ''

    return (
      <div
        data-testid={`react-teaser-container`}
        className={`react-teaser-container ${getSpacing(top, right, bottom, left)} ${lob} ${mcpClass}`}
        id={this.props.id}
      >
        <div
          className={this.props.baseCssClass}
          role='group'
          data-panelcontainer='teaser'
          aria-roledescription='teaser'
        ></div>
        {this.renderTeaser()}
      </div>
    )
  }
}

export default withStandardBaseCssClass(withAuthorPanelSwitch(ReactTeaser), 'cmp-teaser')
